import { TraditionalViabilityRow, TraditionalViabilityV2, TraditionalViabilityV2Row } from "@/types/project";

export const buildViabilityRowKey = (
  scenarioId: string,
  campaignKpi: string,
  channelName: string,
  dmaCode: string
): string => `${scenarioId}::${campaignKpi}::${channelName}::${dmaCode}`;

export function expandTraditionalViabilityV2(
  value: TraditionalViabilityV2 | null | undefined
): TraditionalViabilityRow[] {
  if (!value) return [];

  const result: TraditionalViabilityRow[] = [];

  for (const kpi of value.kpis ?? []) {
    for (const channel of kpi.channels ?? []) {
      for (const row of channel.rows ?? []) {
        const [dma_code, dma_name, source_mode, viability_score, viability_bucket, comment, rank, diagnostics] = row;
        result.push({
          row_key: buildViabilityRowKey(value.scenario_id, kpi.campaign_kpi, channel.channel_name, dma_code),
          scenario_id: value.scenario_id,
          campaign_kpi: kpi.campaign_kpi,
          channel_name: channel.channel_name,
          start_date: channel.start_date,
          end_date: channel.end_date,
          dma_code,
          dma_name,
          source_mode: source_mode ?? "",
          viability_score,
          viability_bucket,
          comment,
          rank,
          diagnostics: diagnostics ?? [],
        });
      }
    }
  }

  return result;
}

const sortRowsByRank = (rows: TraditionalViabilityV2Row[]): TraditionalViabilityV2Row[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const rankA = a.row[6];
      const rankB = b.row[6];
      if (rankA == null && rankB == null) return a.index - b.index;
      if (rankA == null) return 1;
      if (rankB == null) return -1;
      return rankA - rankB;
    })
    .map(({ row }) => row);

export function collapseTraditionalViabilityToV2(
  scenarioId: string,
  rows: TraditionalViabilityRow[]
): TraditionalViabilityV2 {
  const kpiOrder: string[] = [];
  const kpiMap = new Map<
    string,
    {
      channelOrder: string[];
      channelMap: Map<string, { start_date: string; end_date: string; rows: TraditionalViabilityV2Row[] }>;
    }
  >();

  for (const row of rows ?? []) {
    if (!kpiMap.has(row.campaign_kpi)) {
      kpiMap.set(row.campaign_kpi, { channelOrder: [], channelMap: new Map() });
      kpiOrder.push(row.campaign_kpi);
    }
    const kpi = kpiMap.get(row.campaign_kpi)!;

    if (!kpi.channelMap.has(row.channel_name)) {
      kpi.channelMap.set(row.channel_name, { start_date: row.start_date, end_date: row.end_date, rows: [] });
      kpi.channelOrder.push(row.channel_name);
    }
    const channel = kpi.channelMap.get(row.channel_name)!;

    channel.rows.push([
      row.dma_code,
      row.dma_name,
      row.source_mode ?? null,
      row.viability_score,
      row.viability_bucket,
      row.comment,
      row.rank,
      row.diagnostics ?? [],
    ]);
  }

  return {
    scenario_id: scenarioId,
    kpis: kpiOrder.map((campaign_kpi) => {
      const kpi = kpiMap.get(campaign_kpi)!;
      return {
        campaign_kpi,
        channels: kpi.channelOrder.map((channel_name) => {
          const channel = kpi.channelMap.get(channel_name)!;
          return {
            channel_name,
            start_date: channel.start_date,
            end_date: channel.end_date,
            rows: sortRowsByRank(channel.rows),
          };
        }),
      };
    }),
  };
}
